import React, { createContext, useState } from 'react';
import axios from 'axios';

import {
  API_URL,
  LOGO_URL,
  SMALL_LOGO_URL,
  JWT_TOKEN,
  USER_FULL_NAME,
  PERMISSION_KEY,
  ENFORCE_TYPE,
  ENFORCE_PASSWORD_CHANGE,
  LANGUAGE_KEY,
  OTP_TOKEN,
  USER_ID,
  memberBankUser,
} from '../../../constants/appConfig';
import { clearLocalStorage, getLocalStorage, setLocalStorage } from '../../../utils/storageUtil';
import { clearAllPersistedFilters } from 'components/Common/FilterField/filterPersistStore';
import { isAuthenticated } from '../../../utils/jwtUtil';
import configureStore from '../../../store/configureStore';
import history from '../../../utils/history';
import { store as storeRequest } from 'utils/httpUtil';

const store = configureStore({}, history);

const AuthContext = createContext({
  user: {},
  isAuthenticated: false,
  logoutLoading: false,
});

const AuthProvider = props => {
  const [user, setUser] = useState(getLocalStorage('user') || {});
  const [loading, setLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [authenticated, setAuthenticated] = useState(isAuthenticated() || false);
  const state = { user, loading, authenticated, logoutLoading };

  const handleLoginActions = response => {
    setLocalStorage(JWT_TOKEN, response.data.data.token);
    setLocalStorage(memberBankUser, response.data.data.memberBankUser);
    setLocalStorage(LOGO_URL, response.data.data.bankLogoUrl);
    setLocalStorage(SMALL_LOGO_URL, response.data.data.bankLogoThumbnailUrl);
    setLocalStorage(PERMISSION_KEY, response.data.data.permissions);
    setLocalStorage(USER_FULL_NAME, response.data.data.fullName);
    setLocalStorage(USER_ID, response.data.data.userId);
    setLocalStorage(ENFORCE_TYPE, response.data.data.enforceType);
    setLocalStorage(ENFORCE_PASSWORD_CHANGE, response.data.data.enforcePasswordChange);

    setAuthenticated(true);
    if (getLocalStorage(ENFORCE_PASSWORD_CHANGE)) {
      history.push('/profile/change-password');
    } else {
      history.push('/dashboard');
    }
  };

  const handleOTPActions = response => {
    setLocalStorage(OTP_TOKEN, response?.data?.data?.multiFactorAuthentication?.token);
    setLocalStorage(USER_ID, response?.data?.data?.userId);
  };

  const login = ({ userId, password }) => {
    setLoading(true);
    return axios
      .post(API_URL + '/auths/v1/auths', { userId, password })
      .then(response => {
        if (response?.data?.data?.multiFactorAuthentication?.enabled) {
          handleOTPActions(response);
          history.push('/otp');
        } else {
          handleLoginActions(response);
        }
        return response;
      })
      .catch(error => {
        setLoading(false);
        setMessage(error?.response?.data);
      });
  };

  const clearSession = () => {
    store.dispatch({ type: 'LOG_OUT_SUCCESS' });
    clearAllPersistedFilters();
    clearLocalStorage(JWT_TOKEN);
    clearLocalStorage(PERMISSION_KEY);
    clearLocalStorage(USER_FULL_NAME);
    clearLocalStorage(LANGUAGE_KEY);
    clearLocalStorage(ENFORCE_PASSWORD_CHANGE);
    clearLocalStorage(ENFORCE_TYPE);
    clearLocalStorage(LOGO_URL);
    clearLocalStorage(SMALL_LOGO_URL);
    clearLocalStorage(USER_ID);
    clearLocalStorage(OTP_TOKEN);
    setUser({});
    setAuthenticated(false);
    history.push('/dashboard');
  };

  const logout = async () => {
    if (logoutLoading) {
      return;
    }

    setLogoutLoading(true);
    try {
      const response = await storeRequest('auths/v1/logout');
      if (response?.data?.code === '0') {
        clearSession();
      } else {
        console.error('Unexpected logout response:', response?.data);
      }
    } catch (error) {
      console.error('Logout API failed:', error?.response?.data || error.message);
    } finally {
      setLogoutLoading(false);
    }
  };

  const verifyOTP = values => {
    const { otp } = values;
    setLoading();
    return axios
      .get(API_URL + '/auths/v1/auths', {
        params: { totp: otp, token: getLocalStorage(OTP_TOKEN) },
      })
      .then(response => {
        handleLoginActions(response);
      })
      .catch(error => {
        setLoading(false);
        setMessage(error.response.data);
      });
  };

  const resendOTP = () => {
    return axios
      .get(API_URL + '/auths/v1/auths/resend-code', {
        params: { userId: `${getLocalStorage(USER_ID)}`, token: getLocalStorage(OTP_TOKEN) },
      })
      .then(response => {
        setLocalStorage(OTP_TOKEN, response?.data?.data?.multiFactorAuthentication?.token);

        return response;
      })
      .catch(error => {
        setLoading(false);
        setMessage(error.response.data);
      });
  };

  return (
    <AuthContext.Provider
      {...props}
      value={{
        ...state,
        message: message,
        setMessage: setMessage,
        loading: loading,
        setLoading: setLoading,
        login: login,
        logout: logout,
        verifyOTP: verifyOTP,
        resendOTP: resendOTP,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
